import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { getPublishedProjects, sendMessage } from "../lib/api";
import { useSite, githubHandle } from "../lib/site";
import { useAuth } from "../lib/auth";
import { useInlineEdit } from "../lib/inlineEdit";
import { EditableText } from "../components/EditableText";
import { Seo } from "../components/Seo";
import { ProjectCard } from "../components/ProjectCard";
import { Home as HomeIcon, User, FolderGit2, Shield } from "lucide-react";

const DEFAULT_MARQUEE = "GITHUB: OVU-LAB, LINUX ENVIRONMENTS, PYTHON AUTOMATION, WIRESHARK PACKETS, CTF PRACTICES, WEB SECURITY BASICS";

const NAV_CARD_DEFS = [
  { slug: "home", to: "/", path: "~/home", icon: HomeIcon, live: true, tk: "navcard_home", defaultTitle: "Root Terminal", defaultDesc: "Main portal, learning philosophy, and overview." },
  { slug: "about", to: "/about", path: "~/about", icon: User, live: true, tk: "navcard_about", defaultTitle: "Student Dossier", defaultDesc: "High school journey, honest toolset, and lab setup." },
  { slug: "projects", to: "/projects", path: "~/projects", icon: FolderGit2, live: true, tk: "navcard_projects", defaultTitle: "Lab Repositories", defaultDesc: "Published Python tools, bash scripts, and CTF solvers." },
  { slug: "admin", to: "/admin", path: "~/admin", icon: Shield, live: false, tk: "navcard_admin", defaultTitle: "Owner Console", defaultDesc: "Project desk and draft publisher (owner only)." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({ name: "", contact: "", body: "" });
  const [sending, setSending] = useState(false);
  const location = useLocation();
  const { site, loading: siteLoading, loadError: siteLoadError, ready: siteReady } = useSite();
  const { user } = useAuth();
  const { openEditor } = useInlineEdit();
  const marqueeText = site.texts?.home_marquee || DEFAULT_MARQUEE;
  const marqueeItems = marqueeText.split(",").map((s) => s.trim()).filter(Boolean);
  const contactNamePlaceholder =
    site.texts?.home_contact_name_placeholder ?? "Admissions Evaluator";
  const contactEmailPlaceholder =
    site.texts?.home_contact_email_placeholder ?? "officer@college.edu";
  const contactBodyPlaceholder =
    site.texts?.home_contact_body_placeholder ??
    "Inquiring about cybersecurity projects or academic standing...";

  useEffect(() => {
    getPublishedProjects()
      .then((projects) => {
        const feat = projects.filter((p) => p.featured);
        setFeatured((feat.length ? feat : projects).slice(0, 3));
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  // Handle "/#contact" links from other pages
  useEffect(() => {
    if (location.hash === "#contact") {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  const submitContact = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await sendMessage(form);
      toast.success("Message sent — thank you for reaching out.");
      setForm({ name: "", contact: "", body: "" });
    } catch (err) {
      toast.error("Could not send the message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="home-page">
      <Seo
        title="oVu Cyber — Cybersecurity Learning Portfolio"
        description="oVu Cyber is a high school student's cybersecurity learning portfolio — Linux, networking, web security and Python, proven through real, reviewable projects."
      />
      {/* Hero */}
      <section className="relative py-20 md:py-28 px-6 bg-ink border-b border-white/10 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="hero-badge inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 font-mono text-xs text-cyber mb-6" data-testid="hero-badge">
            <span className="w-2 h-2 rounded-full bg-cyber"></span>
            <EditableText k="home_hero_badge" defaultValue="High School Student - My cyber journey" />
          </div>
          <h1 className="font-display text-4xl sm:text-6xl md:text-7xl font-extrabold uppercase text-white tracking-tight leading-[0.98] mb-6">
            <span className="block hero-line-1"><EditableText k="home_hero_line_1" defaultValue="Mastering Security" /></span>
            <span className="block hero-line-2 text-cyber"><EditableText k="home_hero_line_2" defaultValue="Bit By Bit." /></span>
          </h1>
          <p className="hero-foot text-dim text-base md:text-lg max-w-xl mb-8 leading-relaxed">
            <EditableText k="home_hero_subline" multiline defaultValue="Building and auditing hands-on defensive tools in Python and Linux to demonstrate technical rigor for computer science." />
          </p>
          <div className="hero-foot flex flex-wrap items-center gap-4">
            <Link to="/projects" className="px-6 py-3 rounded bg-cyber text-[#0b0f19] font-bold text-xs font-mono uppercase tracking-wider hover:bg-white transition-colors" data-testid="hero-view-projects-button">
              [ <EditableText k="home_hero_btn_projects" defaultValue="View Projects" /> ]
            </Link>
            <Link to="/about" className="px-6 py-3 rounded border border-white/20 text-white font-medium text-xs font-mono uppercase tracking-wider hover:border-cyber hover:text-cyber transition-colors" data-testid="hero-about-button">
              [ <EditableText k="home_hero_btn_about" defaultValue="About Me" /> ]
            </Link>
          </div>
        </div>
      </section>

      {/* Kinetic strip — comma-separated items, editable as one text block */}
      <div
        className={`bg-deep border-b border-white/10 py-3 overflow-hidden ${user ? "cursor-pointer outline-dashed outline-1 outline-transparent hover:outline-cyber/70 transition-[outline-color]" : ""}`}
        aria-hidden={!user}
        aria-disabled={user ? !siteReady : undefined}
        title={
          user
            ? siteLoading
              ? "Loading site settings..."
              : !siteReady
                ? siteLoadError || "Site settings unavailable — click for status"
                : "Click to edit (comma separated items)"
            : undefined
        }
        onClick={user ? () => openEditor("home_marquee", marqueeText, false, DEFAULT_MARQUEE) : undefined}
        data-testid="editable-home_marquee"
      >
        <div className="mq-band font-mono text-xs uppercase text-dim items-center gap-8">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} className="flex items-center gap-8">
              <span className={i % marqueeItems.length === 0 ? "text-white font-bold" : ""}>&gt; {item}</span>
              <span className="text-cyber">&bull;</span>
            </span>
          ))}
        </div>
      </div>

      {/* Site map */}
      <section className="py-20 px-6 bg-deep border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-cyber block mb-2"><EditableText k="home_sitemap_label" defaultValue="// SITE MAP" /></span>
              <h2 className="font-display text-2xl sm:text-3xl text-white font-bold uppercase"><EditableText k="home_sitemap_title" defaultValue="Navigate the Lab" /></h2>
            </div>
            <div className="font-mono text-xs text-dim"><EditableText k="home_sitemap_note" defaultValue="Four routes, one journey" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {NAV_CARD_DEFS.map((card) => (
              <Link key={card.slug} to={card.to} className="p-5 rounded-xl bg-ink border border-white/10 hover:border-cyber transition-colors block group" data-testid={`navcard-${card.slug}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[11px] text-cyber font-bold">
                    <EditableText k={`${card.tk}_path`} defaultValue={card.path} />
                  </span>
                  <span className={`w-2 h-2 rounded-full ${card.live ? "bg-emerald-400" : "bg-amber-400"}`}></span>
                </div>
                <h3 className="font-display text-white font-bold text-sm mb-1 group-hover:text-cyber transition-colors"><EditableText k={`${card.tk}_title`} defaultValue={card.defaultTitle} /></h3>
                <p className="text-xs text-dim"><EditableText k={`${card.tk}_desc`} defaultValue={card.defaultDesc} /></p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About preview */}
      <section className="py-24 px-6 bg-panelalt border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 min-w-0">
              <span className="font-mono text-xs uppercase tracking-widest text-cyber block mb-2"><EditableText k="home_about_label" defaultValue="// 01 ABOUT THE STUDENT" /></span>
              <h2 className="font-display text-3xl sm:text-4xl text-white font-bold uppercase mb-4"><EditableText k="home_about_title" defaultValue="Curious, Focused & Verifiable" /></h2>
              <p className="text-sm text-dim leading-relaxed mb-6">
                {site.bio}
              </p>
              <div className="space-y-3 font-mono text-xs text-[#cbd5e1] mb-8">
                <div className="p-3 rounded bg-ink border border-white/10 flex items-center justify-between gap-4">
                  <span><EditableText k="home_about_toolset_label" defaultValue="Primary Toolset" /></span>
                  <span className="text-cyber text-right">{site.toolset}</span>
                </div>
                <div className="p-3 rounded bg-ink border border-white/10 flex items-center justify-between gap-4">
                  <span><EditableText k="home_about_github_label" defaultValue="GitHub Identity" /></span>
                  <a href={site.github_url} target="_blank" rel="noopener noreferrer" className="text-cyber hover:underline text-right" data-testid="home-github-identity-link">github.com/{githubHandle(site.github_url)}</a>
                </div>
                <div className="p-3 rounded bg-ink border border-white/10 flex items-center justify-between gap-4">
                  <span><EditableText k="home_about_goal_label" defaultValue="Goal" /></span>
                  <span className="text-cyber text-right">{site.goal_text}</span>
                </div>
              </div>
              <Link to="/about" className="font-mono text-xs text-cyber hover:underline uppercase tracking-wider" data-testid="home-about-more-link">
                [ <EditableText k="home_about_link" defaultValue="Full story on the About page →" /> ]
              </Link>
            </div>
            <div className="lg:col-span-6 min-w-0">
              <figure className="relative aspect-[4/3] rounded-xl border border-white/10 overflow-hidden bg-panel">
                <img
                  src="https://images.unsplash.com/photo-1598624443973-2aa79a646a1e?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Hands typing on a backlit mechanical keyboard in a home lab"
                  className="w-full h-full object-cover opacity-80"
                  loading="lazy"
                  data-testid="home-about-image"
                />
                <figcaption className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-deep/90 to-transparent">
                  <span className="font-mono text-xs text-cyber uppercase block">
                    <EditableText k="home_about_image_badge" defaultValue="[Lab Environment Setup]" />
                  </span>
                  <span className="font-mono text-xs text-dim">
                    <EditableText k="home_about_image_caption" defaultValue="Home lab practice with Linux and networking tools" />
                  </span>
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* Featured projects */}
      <section className="py-24 px-6 bg-ink border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-cyber block mb-2"><EditableText k="home_featured_label" defaultValue="// 02 PUBLISHED LABS" /></span>
              <h2 className="font-display text-3xl sm:text-4xl text-white font-bold uppercase"><EditableText k="home_featured_title" defaultValue="Featured Projects" /></h2>
            </div>
            <Link to="/projects" className="font-mono text-xs text-dim hover:text-cyber transition-colors" data-testid="home-all-projects-link">
              [ <EditableText k="home_featured_all" defaultValue="All projects →" /> ]
            </Link>
          </div>
          {loading ? (
            <div className="font-mono text-xs text-dim" data-testid="featured-loading">
              <EditableText k="home_featured_loading" defaultValue="$ loading published projects ..." />
            </div>
          ) : error ? (
            <div className="p-6 rounded-xl bg-panel border border-white/10 font-mono text-xs text-dim" data-testid="featured-error">
              <EditableText k="home_featured_error_cmd" defaultValue="$ fetch projects" />{" "}
              <span className="text-red-400">
                <EditableText k="home_featured_error_msg" defaultValue="-- failed. Please reload the page." />
              </span>
            </div>
          ) : featured.length === 0 ? (
            <div className="p-8 rounded-xl bg-panel border border-white/10 font-mono text-xs" data-testid="featured-empty-state">
              <p className="text-cyber mb-2">
                <EditableText k="home_featured_empty_cmd" defaultValue="$ ls ~/projects/published" />
              </p>
              <p className="text-dim mb-1"><EditableText k="home_featured_empty_1" multiline defaultValue="> 0 entries — first projects are still in the lab." /></p>
              <p className="text-dim"><EditableText k="home_featured_empty_2" multiline defaultValue="They land here the moment they are finished and published. No fake trophies." /></p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="featured-projects-grid">
              {featured.map((p) => (
                <ProjectCard key={p.id} project={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 px-6 bg-ink">
        <div className="max-w-3xl mx-auto text-center">
          <span className="font-mono text-xs uppercase tracking-widest text-cyber block mb-2"><EditableText k="home_contact_label" defaultValue="// 03 CONTACT INQUIRY" /></span>
          <h2 className="font-display text-3xl sm:text-4xl text-white font-bold uppercase mb-4"><EditableText k="home_contact_title" defaultValue="Direct Contact Form" /></h2>
          <p className="text-sm text-dim mb-8">
            <EditableText k="home_contact_sub" multiline defaultValue="Reviewers, teachers and admissions officers can send a direct note below. Submissions are forwarded through Google Forms for review — nothing is stored on this website." />
          </p>
          <form onSubmit={submitContact} className="space-y-4 text-left p-8 rounded-xl bg-panel border border-white/10" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label htmlFor="contact-name" className="block font-mono text-xs text-dim">
                    <EditableText k="home_contact_name_label" defaultValue="Your Name" />
                  </label>
                  {user && (
                    <span className="font-mono text-[10px] text-dim" data-testid="placeholder-trigger-home_contact_name_placeholder">
                      [placeholder:{" "}
                      <EditableText k="home_contact_name_placeholder" defaultValue="Admissions Evaluator" className="text-cyber" />]
                    </span>
                  )}
                </div>
                <input id="contact-name" type="text" required maxLength={80} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-2.5 rounded bg-ink border border-white/10 text-white text-xs font-mono focus:outline-none focus:border-cyber"
                  placeholder={contactNamePlaceholder} data-testid="contact-name-input" />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label htmlFor="contact-email" className="block font-mono text-xs text-dim">
                    <EditableText k="home_contact_email_label" defaultValue="Institution / Email" />
                  </label>
                  {user && (
                    <span className="font-mono text-[10px] text-dim" data-testid="placeholder-trigger-home_contact_email_placeholder">
                      [placeholder:{" "}
                      <EditableText k="home_contact_email_placeholder" defaultValue="officer@college.edu" className="text-cyber" />]
                    </span>
                  )}
                </div>
                <input id="contact-email" type="text" required maxLength={120} value={form.contact}
                  onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  className="w-full px-4 py-2.5 rounded bg-ink border border-white/10 text-white text-xs font-mono focus:outline-none focus:border-cyber"
                  placeholder={contactEmailPlaceholder} data-testid="contact-email-input" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label htmlFor="contact-body" className="block font-mono text-xs text-dim">
                  <EditableText k="home_contact_body_label" defaultValue="Inquiry / Note" />
                </label>
                {user && (
                  <span className="font-mono text-[10px] text-dim" data-testid="placeholder-trigger-home_contact_body_placeholder">
                    [placeholder:{" "}
                    <EditableText
                      k="home_contact_body_placeholder"
                      defaultValue="Inquiring about cybersecurity projects or academic standing..."
                      className="text-cyber"
                    />]
                  </span>
                )}
              </div>
              <textarea id="contact-body" rows={4} required maxLength={2000} value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="w-full px-4 py-2.5 rounded bg-ink border border-white/10 text-white text-xs font-mono focus:outline-none focus:border-cyber"
                placeholder={contactBodyPlaceholder} data-testid="contact-message-input"></textarea>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
              <button type="submit" disabled={sending}
                className="w-full sm:w-auto px-8 py-3 rounded bg-cyber text-[#0b0f19] font-bold text-xs font-mono uppercase tracking-wider hover:bg-white transition-colors disabled:opacity-50"
                data-testid="contact-submit-button">
                {sending ? <EditableText k="home_contact_sending" defaultValue="Sending..." /> : <EditableText k="home_contact_button" defaultValue="Send Message" />}
              </button>
              <span className="font-mono text-xs text-dim">
                <EditableText k="home_contact_github_label" defaultValue="GitHub:" />{" "}
                <a href={site.github_url} target="_blank" rel="noopener noreferrer" className="text-cyber underline" data-testid="contact-github-link">github.com/{githubHandle(site.github_url)}</a>
              </span>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
